import type { DurationFormat } from "../conversions/duration";

const HOURS_IN_DAY = 24;
const DAYS_TO_SHOW_AS_DAYS = 4;
const MINUTES_TO_GIVE_WHEN_DURATION_WAS_ZERO = 1;

type DurationLayout =
  | "exactDays"
  | "exactDaysHours"
  | "exactHours"
  | "exactHoursMinutes"
  | "exactMinutes"
  | "approxDays"
  | "approxDaysHours"
  | "approxDayRangeHours"
  | "approxHours"
  | "approxRoundedHoursMinutes"
  | "approxRoundedMinutesOnly"
  | "approxRangeDays"
  | "approxRangeHours"
  | "approxRangeMinsRoundedBy5"
  | "approxRangeMinsRoundedBy30"
  | "approxHoursRounded30Fraction"
  | "approxFractionOfHourOnly";

const FRACTION_NONE = "";

/**
 * Represent a single duration in multiple units that make up
 * that duration - number days, hours and minutes.
 */
export type TimeUnits = {
  minutes: number;
  hours: number;
  days: number;
  lowerHours: number;
  upperHours: number;
  layout: DurationLayout;
  fraction: string;
  isDaysConvertedToHours: boolean;
};

export function makeTimeUnits(originalDuration: DurationFormat): TimeUnits {
  let time: TimeUnits;
  let init: TimeUnits = {
    days: originalDuration.days,
    hours: originalDuration.hours,
    minutes: originalDuration.minutes,
    layout: "exactDays",
    fraction: FRACTION_NONE,
    lowerHours: 0,
    upperHours: 0,
    isDaysConvertedToHours: false,
  };
  time = convertDaysToHoursWhereNumberIsSmall(init);
  time = setBestExactLayout(time);
  return time;
}

/**
 * Indicates if given time was effectively 0 minutes so special handing is probably
 * needed.
 * @param time - TimeUnits to check
 * @returns true if needs ZERO time handling.
 */
export function isZeroEstimate(time: TimeUnits): boolean {
  return (
    time.days === 0 &&
    time.hours === 0 &&
    (time.minutes === 0 ||
      time.minutes === MINUTES_TO_GIVE_WHEN_DURATION_WAS_ZERO)
  );
}

function hasDays(time: TimeUnits) {
  return time.days > 0;
}
function hasHours(time: TimeUnits) {
  return time.hours > 0 || isHourRange(time);
}
function hasMinutes(time: TimeUnits) {
  return time.minutes > 0;
}
function isHourRange(time: TimeUnits) {
  return time.layout === "approxRangeHours";
}

function isHoursMinutesToBeShownMatchingFormattedDurationBehaviour(
  time: TimeUnits,
) {
  return time.isDaysConvertedToHours || hasMinutes(time);
}

function setBestExactLayout(duration: TimeUnits): TimeUnits {
  let layout: DurationLayout;

  if (hasDays(duration)) {
    if (hasHours(duration)) {
      layout = "exactDaysHours";
    } else {
      layout = "exactDays";
    }
  } else if (hasHours(duration)) {
    if (isHoursMinutesToBeShownMatchingFormattedDurationBehaviour(duration)) {
      layout = "exactHoursMinutes";
    } else {
      layout = "exactHours";
    }
  } else {
    layout = "exactMinutes";
  }
  return { ...duration, layout: layout };
}

function convertDaysToHoursWhereNumberIsSmall(time: TimeUnits): TimeUnits {
  let hours = time.hours;
  let days = time.days;
  let isDaysConvertedToHours: boolean;

  if (hasDays(time) && time.days < DAYS_TO_SHOW_AS_DAYS) {
    hours = hoursWithDaysAsHours(time);
    days = 0;
    isDaysConvertedToHours = true;
  } else {
    isDaysConvertedToHours = false;
  }

  return {
    ...time,
    days: days,
    hours: hours,
    isDaysConvertedToHours: isDaysConvertedToHours,
  };
}

function hoursWithDaysAsHours(time: TimeUnits): number {
  return time.hours + HOURS_IN_DAY * time.days;
}
